import { makeAutoObservable, runInAction } from 'mobx';
import {
  InitialSeedProductTemplate,
  ProductTemplateSortBy,
  ProductTemplateSortOrder,
  InitialSeedTemplateFilter,
} from 'types/productServiceV2';
import { createContext, useContext } from 'react';
import { getInitialSeedProductTemplatesBySponsorId } from 'api/operations/productServiceV2';
import { getRootStoreInstance } from 'store/rootStore';
import { SendOutEmailsModalStore } from 'store/general/sendOutEmailsModalStore';
import { generateMaintenance } from 'api/operations/biReportService';
import { FileType } from 'types/biReportService';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { PaginationDelegate } from '../../../../general/paginationDelegate';
import ErrorMessageModalDelegate from './errorMessageModalDelegate';
import UploadJSONModalDelegate from './uploadJSONModalDelegate';

export default class InitialSeedProductTemplatesStore {
  private _rootStore = getRootStoreInstance();
  private _isLoading = false;
  private _productTemplates: InitialSeedProductTemplate[] = [];

  public paginationDelegate = new PaginationDelegate<
    ProductTemplateSortBy,
    ProductTemplateSortOrder,
    InitialSeedTemplateFilter
  >({
    defaultSortBy: ProductTemplateSortBy.NAME,
    defaultSortOrder: ProductTemplateSortOrder.DESC,
    filters: { productName: null, doesNotHaveBenefitsFile: null, hasValidationErrors: null },
  });

  public errorMessageModalDelegate = new ErrorMessageModalDelegate();
  public uploadJsonModalDelegate = new UploadJSONModalDelegate();
  public groupMaintenanceReportsModalStore = new SendOutEmailsModalStore({
    onSubmit: async (recipients: string[]): Promise<void> => {
      await generateMaintenance(FileType.GROUP, recipients);
      toastManager.addToast(
        'Report generation was successfully requested, please check your email soon!',
        ToastColorTypes.SUCCESS,
      );
    },
  });

  public rateMaintenanceReportsModalStore = new SendOutEmailsModalStore({
    onSubmit: async (recipients: string[]): Promise<void> => {
      await generateMaintenance(FileType.RATE, recipients);
      toastManager.addToast(
        'Report generation was successfully requested, please check your email soon!',
        ToastColorTypes.SUCCESS,
      );
    },
  });

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get productTemplates(): InitialSeedProductTemplate[] {
    return this._productTemplates;
  }

  public setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public async getInitialData(): Promise<void> {
    this.setIsLoading(true);
    const initialDataInput = {
      sponsorId: this._rootStore.domainStoreV2.sponsorId,
      ...this.paginationDelegate.paginationInput,
    };
    const result = await getInitialSeedProductTemplatesBySponsorId(initialDataInput);
    runInAction(() => {
      this.paginationDelegate.setTotalElements(result.totalElements);
      this.paginationDelegate.setTotalPages(result.totalPages);
      this._productTemplates = result.result;

      this.setIsLoading(false);
    });
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(new InitialSeedProductTemplatesStore());

export const useInitialSeedProductTemplatesStore = (): InitialSeedProductTemplatesStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
